import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { formatNumber } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';
import InventoryByProductIdDialog from './InventoryByProductIdDialog';


const ProductInventoryBySectorType = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [openInventoryByProductIdDialog, setOpenInventoryByProductIdDialog] = useState(false);
    const [selectedProductName, setSelectedProductName] = useState(false);

    const [productsInventory, setProductsInventory] = useState([]);
    const [inventoryByProductId, setInventoryByProductId] = useState([]);


    useEffect(() => {
        const handleProductInventory = async () => {
            if (!startDate || !endDate) return [];

            try {
                setError(null);
                setLoading(true);
                const url = '/inflightsales/productinventorybysectortype';
                const payload = { startDate, endDate, timeMode };
                const res = await api.post(url, payload);
                setProductsInventory(res.data);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        handleProductInventory();
    }, [timeMode, startDate, endDate]);


    const handleViewClick = useCallback(async (productId) => {
        try {
            const url = '/inflightsales/inventorybyproductid';
            const payload = { productId, startDate, endDate, timeMode };
            const res = await api.post(url, payload);

            setInventoryByProductId(res.data);
            setOpenInventoryByProductIdDialog(true);
        } catch (error) {
            toast.error(error || "Failed to fetch inventory by ProductId!");
        }
    }, [timeMode, startDate, endDate]);

    const handleDialogClose = () => setOpenInventoryByProductIdDialog(false);

    const handleExcelJSDownload = () => {
        if (!productsInventory || productsInventory.length === 0) {
            toast.error("No data available to export.");
            return;
        }
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14);
        const fileName = `ProductsInventory_${timestamp}.xlsx`;

        const filteredInventory = productsInventory?.map(product => {
            const { productId, ...rest } = product;
            return { ...rest };
        });

        exportToExcel(filteredInventory, fileName);
    };

    const columns = useMemo(() => {
        const commonNumericConfig = {
            headerClassName: 'theme-column--header',
            align: 'right',
            width: 135,
            valueFormatter: (params) => params ? Math.ceil(params).toLocaleString() : ''
        };

        let baseColumns = [
            {
                field: "no",
                headerName: "No",
                headerClassName: 'theme-column--header',
                filterable: false,
                sortable: false,
                width: 60,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
            },
            {
                field: "sectorType",
                headerName: "SectorType",
                headerClassName: 'theme-column--header',
                width: 200
            },
            {
                field: "productName",
                headerName: "ProductName",
                headerClassName: 'theme-column--header',
                width: 300
            },
            {
                field: "category",
                headerName: "Category",
                headerClassName: 'theme-column--header',
                width: 100
            },
            {
                field: "unit",
                headerName: "Unit",
                headerClassName: 'theme-column--header',
                width: 100
            },
            ...["preorder", "direct"].flatMap(prefix =>
                ["StockIn", "PreviousReceipt", "NextRetrieval", "StockOn", "Shrinkage", "Combo", "Loss", "Consume"]?.map(field => ({
                    field: `${prefix}${field}`,
                    headerName: `${prefix.charAt(0).toUpperCase() + prefix.slice(1)}${field}`,
                    ...commonNumericConfig
                }))
            ),
            { field: "purchasePrice", headerName: "PurchasePrice", ...commonNumericConfig },
            { field: "cost", headerName: "Cost", ...commonNumericConfig },
            { field: "sellPrice", headerName: "SellPrice", ...commonNumericConfig },
            { field: "revenue", headerName: "Revenue", ...commonNumericConfig },
            { field: "lossShrinkage", headerName: "LossShrinkage", ...commonNumericConfig },
        ];

        baseColumns.splice(1, 0, {
            field: "supplierName",
            headerName: "SupplierName",
            headerClassName: 'theme-column--header',
            width: 150
        });

        return baseColumns;
    }, []);

    const actionColumn = useMemo(() => [{
        field: "action",
        headerName: "Action",
        width: 80,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
            <FlexCenter gap={2} sx={{ width: '100%', height: '100%' }}>
                <Tooltip title="View">
                    <IconButton
                        aria-label="view"
                        sx={{
                            color: theme.palette.primary.dark,
                            cursor: 'pointer',
                            '&:hover': { color: theme.palette.green.deep },
                        }}
                        onClick={() => {
                            setSelectedProductName(params?.row?.productName);
                            handleViewClick(params?.row?.productId);
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Tooltip>
            </FlexCenter>
        ),
    }], [theme, handleViewClick]);


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Inventory by Type"
                    subtitle={`${loading ? 'Loading...' : 'List of Inventory Products'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton
                                    onClick={() => {
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "ProductsInventoryBySectorType", format: "Excel", startDate, endDate });
                                    }}
                                >
                                    <DownloadIcon />
                                </StyledButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={productsInventory}
                                primaryField="productName"
                                secondaryField="sectorType"
                                minWidth={105}
                                fields={[
                                    { label: "Supplier",          key: "supplierName" },
                                    { label: "Category",          key: "category" },
                                    { label: "Unit",              key: "unit" },
                                    { label: "Preorder StockIn",  key: "preorderStockIn",  cols: 2, render: (r) => formatNumber(r.preorderStockIn) },
                                    { label: "Direct StockIn",    key: "directStockIn",    cols: 2, render: (r) => formatNumber(r.directStockIn) },
                                    { label: "Preorder Consume",  key: "preorderConsume",  cols: 2, render: (r) => formatNumber(r.preorderConsume) },
                                    { label: "Direct Consume",    key: "directConsume",    cols: 2, render: (r) => formatNumber(r.directConsume) },
                                    { label: "Revenue",           key: "revenue",                   render: (r) => formatNumber(r.revenue) },
                                ]}
                                isActiveField={null}
                                canView
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const item = productsInventory.find(p => String(p._id) === String(id));
                                    if (item) {
                                        setSelectedProductName(item.productName);
                                        handleViewClick(item.productId);
                                    }
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={productsInventory}
                                columns={[...columns, ...actionColumn]}
                                loading={loading}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {openInventoryByProductIdDialog &&
                <InventoryByProductIdDialog
                    open={openInventoryByProductIdDialog}
                    onClose={handleDialogClose}
                    selectedProductName={selectedProductName}
                    inventoryProducts={inventoryByProductId}
                />
            }
        </Box>
    );
};

export default ProductInventoryBySectorType;
